import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SESSION_MS = 2 * 60 * 60 * 1000;       // 2 hours
const WARN_BEFORE_MS = 5 * 60 * 1000;        // 5 minutes

/**
 * Global session-expiry heads-up. Renders a dialog 5 minutes before the
 * 2-hour access token expires with a "Stay signed in" refresh action.
 * Uses the sessionStartedAt timestamp from AuthContext (set on login /
 * register / checkAuth) and hits POST /auth/refresh to rotate the token.
 */
export default function SessionExpiryWarning() {
  const { user, sessionStartedAt, refreshSession, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const warnTimerRef = useRef(null);
  const expireTimerRef = useRef(null);
  const tickRef = useRef(null);

  useEffect(() => {
    clearTimeout(warnTimerRef.current);
    clearTimeout(expireTimerRef.current);
    clearInterval(tickRef.current);
    setOpen(false);

    if (!user || !sessionStartedAt) return;

    const now = Date.now();
    const elapsed = now - sessionStartedAt;
    const warnAt = SESSION_MS - WARN_BEFORE_MS - elapsed;
    const expireAt = SESSION_MS - elapsed;

    const showWarning = () => {
      setSecondsLeft(Math.max(1, Math.round((SESSION_MS - (Date.now() - sessionStartedAt)) / 1000)));
      setOpen(true);
      tickRef.current = setInterval(() => {
        const left = Math.round((SESSION_MS - (Date.now() - sessionStartedAt)) / 1000);
        setSecondsLeft(Math.max(0, left));
      }, 1000);
    };

    if (warnAt <= 0 && expireAt > 0) {
      showWarning();
    } else if (warnAt > 0) {
      warnTimerRef.current = setTimeout(showWarning, warnAt);
    }

    if (expireAt > 0) {
      expireTimerRef.current = setTimeout(async () => {
        clearInterval(tickRef.current);
        setOpen(false);
        toast.error('Your session has expired. Please sign in again.');
        try { await logout(); } catch (_) { /* already logged out */ }
      }, expireAt);
    }

    return () => {
      clearTimeout(warnTimerRef.current);
      clearTimeout(expireTimerRef.current);
      clearInterval(tickRef.current);
    };
  }, [user, sessionStartedAt, logout]);

  const handleStay = async () => {
    try {
      await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      refreshSession();
      setOpen(false);
      toast.success('Session extended for another 2 hours.');
    } catch (e) {
      toast.error('Could not extend session. Please sign in again.');
    }
  };

  const handleSignOut = async () => {
    setOpen(false);
    try { await logout(); } catch (_) { /* noop */ }
  };

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="session-expiry-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Your session is about to expire
          </DialogTitle>
          <DialogDescription>
            For your security you'll be signed out in{' '}
            <span data-testid="session-expiry-countdown" className="font-semibold text-foreground">
              {mins}:{secs.toString().padStart(2, '0')}
            </span>
            . Please save your work or stay signed in to extend your session another 2 hours.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleSignOut} data-testid="session-expiry-signout">
            Sign out now
          </Button>
          <Button onClick={handleStay} data-testid="session-expiry-stay">
            Stay signed in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
